import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Sortieren durch Ziehen — mit Zeiger und mit Tastatur.
 *
 * Kein Fremdpaket: Eine Offline-App trägt jedes Kilobyte mit sich herum, und gebraucht
 * wird hier eine einzige Liste. Statt HTML5-Drag-and-Drop kommen Pointer-Events zum
 * Einsatz — `dragstart` löst auf Touchgeräten gar nicht erst aus, und die App wird
 * überwiegend auf Telefonen bedient.
 *
 * **Die Tastaturbedienung ist kein Zusatz, sondern Ersatz.** Vorher standen an jeder
 * Kachel zwei Schaltflächen, die sich mit der Tabulatortaste erreichen ließen. Eine
 * gehaltene Zeigergeste kann das nicht: Sie ist für Tastatur und Sprachsteuerung
 * unerreichbar. Der Griff nimmt deshalb auf Leertaste auf, verschiebt mit den
 * Pfeiltasten und legt auf Leertaste wieder ab.
 */

export type Griff = {
  onPointerDown: (event: React.PointerEvent<HTMLElement>) => void
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void
  'aria-pressed': boolean
  style: { touchAction: 'none' }
}

export type Sortierung = {
  /** Reihenfolge, die angezeigt werden soll — während des Sortierens die Vorschau. */
  reihenfolge: readonly string[]
  /** Was gerade bewegt wird; hebt den Eintrag optisch hervor. */
  aktiv: string | null
  /** Verschiebung des bewegten Eintrags gegenüber seinem Platz, in Pixeln. */
  versatz: { x: number; y: number }
  /** Für die Sprachausgabe: was zuletzt geschah. */
  ansage: string
  /** Merkt sich das DOM-Element eines Eintrags. */
  merke: (id: string) => (el: HTMLElement | null) => void
  /** Eigenschaften für den Ziehgriff eines Eintrags. */
  griff: (id: string) => Griff
}

function verschiebe(ids: readonly string[], von: number, nach: number): string[] {
  const next = [...ids]
  const [bewegt] = next.splice(von, 1)
  if (bewegt === undefined) return next
  next.splice(nach, 0, bewegt)
  return next
}

function gleich(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((wert, i) => wert === b[i])
}

/**
 * @param ids Reihenfolge, wie sie gespeichert ist.
 * @param speichern Wird mit der neuen Reihenfolge aufgerufen, sobald abgelegt wurde.
 * @param benennen Titel zu einer Kennung — nur für die Ansagen.
 */
export function useSortieren(
  ids: readonly string[],
  speichern: (ids: readonly string[]) => void,
  benennen: (id: string) => string,
): Sortierung {
  /** Vorschau während des Sortierens; `null`, solange nichts bewegt wird. */
  const [entwurf, setEntwurf] = useState<readonly string[] | null>(null)
  const [aktiv, setAktiv] = useState<string | null>(null)
  const [versatz, setVersatz] = useState({ x: 0, y: 0 })
  const [ansage, setAnsage] = useState('')

  const elemente = useRef(new Map<string, HTMLElement>())
  /** Zeiger- und Mittelpunktlage beim Aufnehmen — Bezugspunkt für die Verschiebung. */
  const start = useRef({ zeiger: { x: 0, y: 0 }, mitte: { x: 0, y: 0 } })
  /** Mit der Tastatur aufgenommen: Pfeiltasten verschieben, Leertaste legt ab. */
  const [gehalten, setGehalten] = useState<string | null>(null)

  // Der Entwurf bleibt stehen, bis die gespeicherte Reihenfolge nachgezogen ist. Ihn
  // beim Ablegen sofort zu verwerfen ließe für einen Wimpernschlag die alte
  // Reihenfolge aufblitzen, weil die Datenbank erst danach antwortet.
  //
  // Er verfällt, sobald sich die *Menge* der Favoriten ändert — ein hinzugekommener
  // oder entfernter Favorit macht jede Vorschau ungültig, und dann gilt wieder das,
  // was gespeichert ist. Abgeleitet statt in einem Effekt zurückgesetzt: Ein Effekt
  // liefe erst nach dem Rendern, also einen Bildaufbau zu spät.
  const entwurfGilt =
    entwurf !== null && entwurf.length === ids.length && entwurf.every((id) => ids.includes(id))
  const reihenfolge = entwurfGilt ? entwurf : ids

  const merke = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) elemente.current.set(id, el)
      else elemente.current.delete(id)
    },
    [],
  )

  /** Index, dessen Mittelpunkt dem Zielpunkt am nächsten liegt. */
  const zielIndex = useCallback(
    (liste: readonly string[], ziel: { x: number; y: number }, aktivId: string): number => {
      let beste = liste.indexOf(aktivId)
      let kleinster = Infinity
      liste.forEach((id, index) => {
        const el = elemente.current.get(id)
        if (!el) return
        const rect = el.getBoundingClientRect()
        // Der bewegte Eintrag trägt eine Verschiebung; für den Vergleich zählt sein
        // Platz in der Liste, nicht seine Lage unter dem Finger.
        const korrektur = id === aktivId ? versatz : { x: 0, y: 0 }
        const mx = rect.left - korrektur.x + rect.width / 2
        const my = rect.top - korrektur.y + rect.height / 2
        const abstand = (mx - ziel.x) ** 2 + (my - ziel.y) ** 2
        if (abstand < kleinster) {
          kleinster = abstand
          beste = index
        }
      })
      return beste
    },
    [versatz],
  )

  const aufnehmen = useCallback(
    (id: string, zeiger: { x: number; y: number }) => {
      const el = elemente.current.get(id)
      if (!el) return
      const rect = el.getBoundingClientRect()
      start.current = {
        zeiger,
        mitte: { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 },
      }
      setVersatz({ x: 0, y: 0 })
      setAktiv(id)
      setEntwurf([...reihenfolge])
    },
    [reihenfolge],
  )

  /** Verschiebt den aufgenommenen Eintrag an die Stelle, über der der Zeiger steht. */
  const bewege = useCallback(
    (id: string, zeigerX: number, zeigerY: number) => {
      const liste = entwurf ?? reihenfolge
      const ziel = {
        x: start.current.mitte.x + (zeigerX - start.current.zeiger.x),
        y: start.current.mitte.y + (zeigerY - start.current.zeiger.y),
      }

      const el = elemente.current.get(id)
      if (el) {
        // Basislage ohne die aktuelle Verschiebung — sie ändert sich, sobald die
        // Vorschau umsortiert. Nur so bleibt der Eintrag unter dem Finger, statt bei
        // jedem Platzwechsel wegzuspringen.
        const rect = el.getBoundingClientRect()
        const basis = {
          x: rect.left - versatz.x + rect.width / 2,
          y: rect.top - versatz.y + rect.height / 2,
        }
        setVersatz({ x: ziel.x - basis.x, y: ziel.y - basis.y })
      }

      const von = liste.indexOf(id)
      const nach = zielIndex(liste, ziel, id)
      if (nach !== von && nach >= 0) setEntwurf(verschiebe(liste, von, nach))
    },
    [entwurf, reihenfolge, versatz, zielIndex],
  )

  const abbrechen = useCallback(() => {
    setAktiv(null)
    setGehalten(null)
    setVersatz({ x: 0, y: 0 })
    setEntwurf(null)
  }, [])

  const ablegen = useCallback(() => {
    setAktiv(null)
    setGehalten(null)
    setVersatz({ x: 0, y: 0 })
    if (entwurf && !gleich(entwurf, ids)) speichern(entwurf)
    else setEntwurf(null)
  }, [entwurf, ids, speichern])

  /**
   * Bewegung und Loslassen hängen am Fenster, nicht am Griff.
   *
   * Der Griff wandert beim Umsortieren im DOM an eine andere Stelle. Dabei verliert er
   * seine Pointer Capture, und `pointerup` erreicht ihn nicht mehr — die Reihenfolge
   * sähe richtig aus, würde aber nie gespeichert. Am Fenster kann das nicht passieren.
   */
  useEffect(() => {
    if (!aktiv) return
    const bewegen = (event: PointerEvent) => bewege(aktiv, event.clientX, event.clientY)
    const loslassen = () => ablegen()
    window.addEventListener('pointermove', bewegen)
    window.addEventListener('pointerup', loslassen)
    // Eine abgebrochene Geste — etwa ein eingehender Anruf — speichert nichts.
    window.addEventListener('pointercancel', abbrechen)
    return () => {
      window.removeEventListener('pointermove', bewegen)
      window.removeEventListener('pointerup', loslassen)
      window.removeEventListener('pointercancel', abbrechen)
    }
  }, [abbrechen, ablegen, aktiv, bewege])

  const griff = useCallback(
    (id: string): Griff => ({
      onPointerDown: (event) => {
        // Nur die primäre Taste; ein Rechtsklick soll nichts aufnehmen.
        if (event.button !== 0) return
        // Verhindert, dass der Browser die Geste als Textauswahl oder Bildlauf deutet.
        event.preventDefault()
        aufnehmen(id, { x: event.clientX, y: event.clientY })
      },

      onKeyDown: (event) => {
        const liste = entwurf ?? reihenfolge
        const von = liste.indexOf(id)

        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault()
          if (gehalten === id) {
            ablegen()
            setAnsage(`„${benennen(id)}" abgelegt auf Platz ${von + 1} von ${liste.length}.`)
          } else {
            setGehalten(id)
            setEntwurf([...liste])
            setAnsage(
              `„${benennen(id)}" aufgenommen, Platz ${von + 1} von ${liste.length}. ` +
                'Mit den Pfeiltasten verschieben, mit der Leertaste ablegen.',
            )
          }
          return
        }

        if (event.key === 'Escape' && gehalten === id) {
          event.preventDefault()
          setGehalten(null)
          setEntwurf(null)
          setAnsage(`Verschieben abgebrochen. „${benennen(id)}" bleibt, wo es war.`)
          return
        }

        if (gehalten !== id) return

        const richtung =
          event.key === 'ArrowLeft' || event.key === 'ArrowUp'
            ? -1
            : event.key === 'ArrowRight' || event.key === 'ArrowDown'
              ? 1
              : 0
        if (richtung === 0) return

        event.preventDefault()
        const nach = von + richtung
        if (nach < 0 || nach >= liste.length) return
        setEntwurf(verschiebe(liste, von, nach))
        setAnsage(`„${benennen(id)}" auf Platz ${nach + 1} von ${liste.length}.`)
      },

      'aria-pressed': gehalten === id,
      style: { touchAction: 'none' },
    }),
    [ablegen, aufnehmen, benennen, entwurf, gehalten, reihenfolge],
  )

  return { reihenfolge, aktiv: aktiv ?? gehalten, versatz, ansage, merke, griff }
}
